import { select, Selection } from 'd3-selection'
import { BehaviorSubject } from 'rxjs'
import pointService from 'live/services/charts/point'
import { css } from 'emotion'
import { widgetType, Options } from '../../types'
import { AnonymousSubject } from 'rxjs/internal/Subject'

// TODO: refactory

const elCss = css`
    > div {
        height: 100%;
        user-select: none;
    }
`

interface BaseConstructor {
    el: HTMLElement
    options: Options
    subject: AnonymousSubject<T>
    state$: BehaviorSubject<T>
    dispatch?: (event: any) => void
}
export default abstract class BaseChart<T> {
    protected el: Selection<HTMLElement, unknown, null, undefined>
    protected options: Options
    protected subject: BehaviorSubject<T>
    protected dispatch?: (event: any) => void

    constructor({ el, options, subject, state$ }: BaseConstructor) {
        this.el = select(el).classed(elCss, true)

        this.options = options
        this.subject = state$
        this.dispatch = event => subject.next(event)

        if (!this.options.cfg?.numberOfVisibleAxes) {
            this.options.cfg = { ...options.cfg, numberOfVisibleAxes: this.numberOfVisibleAxes }
        }
    }

    protected get widgetId(): number {
        return this.options.id
    }

    abstract init(): void

    abstract destroy(): void

    get margin(): { top: number; left: number; right: number; bottom: number } {
        return {
            top: this.isVertical ? 10 : 5,
            left: this.isVertical ? 0 : 10,
            right: this.isVertical ? 5 : 10,
            bottom: this.isVertical ? 5 : 0
        }
    }

    /**
     * Get numberOfVisibleAxes from other widget
     */
    get numberOfVisibleAxes(): number {
        const cfg = this.options.cfg
        if (cfg && cfg.numberOfVisibleAxes) {
            return Number(cfg.numberOfVisibleAxes)
        }

        return this.numberOfVisibleAxesFromNeighbor
    }

    get numberOfVisibleAxesFromNeighbor(): number {
        const defaultNumberOfVisibleAxes = 6

        const dashboardContext = this.options.dashboardContext
        if (dashboardContext) {
            if (dashboardContext.dashboard) {
                const widgets: Array<widgetType> = dashboardContext.dashboard.widgets
                if (widgets) {
                    for (const widgetIndex in widgets) {
                        const jsonConfig = widgets[widgetIndex].jsonConfig
                        if (jsonConfig?.numberOfVisibleAxes && jsonConfig.verticalEnabled === this.isVertical) {
                            return Number(jsonConfig.numberOfVisibleAxes)
                        }
                    }
                }
            }
        }

        return defaultNumberOfVisibleAxes
    }

    /**
     * Update dashboard span
     * @param span
     */
    updateSpan(span: { start: number; end: number }): void {
        if (!this.options.dashboardContext.actions) {
            // widget editor
            return
        }
        this.options.dashboardContext.actions.updateSpan('apply', span, this.options.widgetIndex)
        // This event should be used to cleanup state and fix double/render See: ResizeObserver notes.
    }

    highlightAll(value: number): void {
        const point = { x: value, chart: this }

        const hasNewApi = Boolean(pointService.currentPoint$)

        if (hasNewApi) {
            pointService?.currentPoint$?.next?.(point)
        } else {
            pointService.highlightAll(point)
            pointService.emit('mouseover', { target: { x: value } }, this, [])
        }
    }

    get isVertical(): boolean | null {
        return this.options.cfg?.verticalEnabled
    }

    get spanPickerType(): string {
        return this.options?.cfg?.spanPicker?.type || 'time'
    }

    get hideXAxis(): boolean {
        return this.options.cfg?.hideXAxis || false
    }

    get width(): number | undefined {
        return this.el.node()?.getBoundingClientRect().width
    }

    get height(): number | undefined {
        return this.el.node()?.getBoundingClientRect().height
    }

    get theme(): string {
        return this.options.dashboardTheme
    }

    abstract render(date: T, options: any): void
}
